import { supabase } from "../lib/supabase";
import { MasterContainer, MasterContainerWithContainers } from "../types/booking";

/**
 * Fetch all master containers with their associated containers
 */
export const fetchMasterContainers = async () => {
  try {
    const { data, error } = await supabase
      .from("master_containers")
      .select(`
        *,
        master_containers_containers(
          container_id,
          containers(
            id,
            reference,
            status,
            etd,
            eta,
            track_and_trace
          )
        )
      `)
      .order("created_at", { ascending: false });

    if (error) throw error;

    // Transform the data to include container count and flatten containers
    const transformedData = data?.map((masterContainer: MasterContainer & {
      master_containers_containers?: Array<{
        container_id: number;
        containers: {
          id: number;
          reference: string | null;
          status: string | null;
          etd: string | null;
          eta: string | null;
          track_and_trace: string | null;
        };
      }> | null;
    }) => ({
      ...masterContainer,
      containers: masterContainer.master_containers_containers?.map((mcc) => mcc.containers).filter(Boolean) || [],
      container_count: masterContainer.master_containers_containers?.length || 0,
    })) || [];

    return { data: transformedData as MasterContainerWithContainers[], error: null };
  } catch (error) {
    console.error("Error fetching master containers:", error);
    return { data: [], error };
  }
};

/**
 * Create a new master container
 */
export const createMasterContainer = async (masterContainerData: {
  reference: string;
  eta_customer?: string | null;
  eta?: string | null;
  etd?: string | null;
  status?: string | null;
  track_and_trace?: string | null;
}) => {
  try {
    const { data, error } = await supabase
      .from("master_containers")
      .insert(masterContainerData)
      .select()
      .single();

    if (error) throw error;

    return { data: data as MasterContainer, error: null };
  } catch (error) {
    console.error("Error creating master container:", error);
    return { data: null, error };
  }
};

/**
 * Update a master container
 */
export const updateMasterContainer = async (id: number, masterContainerData: {
  reference?: string;
  eta_customer?: string | null;
  eta?: string | null;
  etd?: string | null;
  status?: string | null;
  track_and_trace?: string | null;
}) => {
  try {
    const { data, error } = await supabase
      .from("master_containers")
      .update(masterContainerData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return { data: data as MasterContainer, error: null };
  } catch (error) {
    console.error("Error updating master container:", error);
    return { data: null, error };
  }
};

/**
 * Fetch master containers for filter dropdown (simplified)
 */
export const fetchMasterContainersForFilter = async () => {
  try {
    const { data, error } = await supabase
      .from("master_containers")
      .select("id, reference")
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error("Error fetching master containers for filter:", error);
    return { data: [], error };
  }
};

/**
 * Bulk update containers in a master container with track_and_trace option
 */
export const bulkUpdateContainersInMaster = async (
  masterContainerId: number,
  updates: {
    eta_customer?: string | null;
    eta?: string | null;
    etd?: string | null;
    status?: string | null;
    track_and_trace?: string | null;
  }
) => {
  try {
    // First get all container IDs in this master container
    const { data: containerRelations, error: relationError } = await supabase
      .from("master_containers_containers")
      .select("container_id")
      .eq("master_container_id", masterContainerId);

    if (relationError) throw relationError;

    const containerIds = containerRelations?.map(rel => rel.container_id).filter(Boolean) || [];

    if (containerIds.length === 0) {
      return { success: true, updatedContainers: 0, error: null };
    }

    // Update all containers in this master container
    const { data, error } = await supabase
      .from("containers")
      .update(updates)
      .in("id", containerIds)
      .select("id, reference, status, eta, eta_customer, etd, track_and_trace");

    if (error) throw error;

    return { success: true, updatedContainers: data?.length || 0, data, error: null };
  } catch (error) {
    console.error("Error bulk updating containers:", error);
    return { success: false, updatedContainers: 0, data: null, error };
  }
};

/**
 * Bulk update bookings in containers of a master container
 */
export const bulkUpdateBookingsInMaster = async (
  masterContainerId: number,
  updates: {
    status?: string | null;
  }
) => {
  try {
    // First get all container IDs in this master container
    const { data: containerRelations, error: relationError } = await supabase
      .from("master_containers_containers")
      .select("container_id")
      .eq("master_container_id", masterContainerId);

    if (relationError) throw relationError;

    const containerIds = containerRelations?.map(rel => rel.container_id).filter(Boolean) || [];

    if (containerIds.length === 0) {
      return { success: true, updatedBookings: 0, error: null };
    }

    // Update all bookings in these containers
    const { data, error } = await supabase
      .from("bookings")
      .update(updates)
      .in("container_id", containerIds)
      .select("id, reference, status, container_id");

    if (error) throw error;

    return { success: true, updatedBookings: data?.length || 0, data, error: null };
  } catch (error) {
    console.error("Error bulk updating bookings:", error);
    return { success: false, updatedBookings: 0, data: null, error };
  }
};

/**
 * Get preview of what will be updated in a master container
 */
export const getUpdatePreview = async (masterContainerId: number) => {
  try {
    // Get containers in master container
    const { data: containerData, error: containerError } = await supabase
      .from("master_containers_containers")
      .select(`
        container_id,
        containers(
          id,
          reference,
          status,
          eta,
          eta_customer,
          etd,
          track_and_trace
        )
      `)
      .eq("master_container_id", masterContainerId);

    if (containerError) throw containerError;

    const containers = containerData?.map(rel => rel.containers).filter(Boolean).flat() || [];
    const containerIds = containers.map(c => c.id);

    // Get bookings in these containers
    const { data: bookingData, error: bookingError } = await supabase
      .from("bookings")
      .select("id, reference, status, container_id")
      .in("container_id", containerIds);

    if (bookingError) throw bookingError;

    return {
      containers: containers,
      bookings: bookingData || [],
      error: null
    };
  } catch (error) {
    console.error("Error getting update preview:", error);
    return { containers: [], bookings: [], error };
  }
};

/**
 * Delete a master container and all its relationships
 */
export const deleteMasterContainer = async (id: number) => {
  try {
    // First delete all container relationships
    const { error: relationError } = await supabase
      .from("master_containers_containers")
      .delete()
      .eq("master_container_id", id);

    if (relationError) throw relationError;

    // Then delete the master container
    const { error } = await supabase
      .from("master_containers")
      .delete()
      .eq("id", id);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting master container:", error);
    return { success: false, error };
  }
};

/**
 * Add a container to a master container
 */
export const addContainerToMaster = async (masterId: number, containerId: number) => {
  try {
    // Check if the relationship already exists
    const { data: existing, error: checkError } = await supabase
      .from("master_containers_containers")
      .select("id")
      .eq("master_container_id", masterId)
      .eq("container_id", containerId)
      .single();

    if (checkError && checkError.code !== "PGRST116") {
      throw checkError;
    }

    if (existing) {
      return { success: false, error: new Error("Container is already in this master container") };
    }

    const { data, error } = await supabase
      .from("master_containers_containers")
      .insert({
        master_container_id: masterId,
        container_id: containerId,
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, data, error: null };
  } catch (error) {
    console.error("Error adding container to master:", error);
    return { success: false, data: null, error };
  }
};

/**
 * Remove a container from a master container
 */
export const removeContainerFromMaster = async (masterId: number, containerId: number) => {
  try {
    const { error } = await supabase
      .from("master_containers_containers")
      .delete()
      .eq("master_container_id", masterId)
      .eq("container_id", containerId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error("Error removing container from master:", error);
    return { success: false, error };
  }
};

/**
 * Get available containers (not assigned to any master container)
 */
export const getAvailableContainers = async () => {
  try {
    // First get all container IDs that are already assigned to master containers
    const { data: assignedContainerIds, error: assignedError } = await supabase
      .from("master_containers_containers")
      .select("container_id")
      .not("container_id", "is", null);

    if (assignedError) throw assignedError;

    // Extract the IDs into an array
    const assignedIds = assignedContainerIds?.map(item => item.container_id) || [];

    // Now get containers that are not in the assigned list
    let query = supabase
      .from("containers")
      .select(`
        id,
        reference,
        status,
        etd,
        eta,
        track_and_trace,
        transport_type_info:booking_transport_types(name, abbreviation)
      `);

    // Only add the not in filter if there are assigned containers
    if (assignedIds.length > 0) {
      query = query.not("id", "in", `(${assignedIds.join(",")})`);
    }

    const { data, error } = await query.order("created_at", { ascending: false });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error("Error fetching available containers:", error);
    return { data: [], error };
  }
};

/**
 * Get containers that belong to a specific master container
 */
export const getContainersByMasterContainer = async (masterContainerId: number) => {
  try {
    const { data, error } = await supabase
      .from("master_containers_containers")
      .select("container_id")
      .eq("master_container_id", masterContainerId);

    if (error) throw error;

    const containerIds = data?.map(item => item.container_id).filter(Boolean) || [];
    return { data: containerIds, error: null };
  } catch (error) {
    console.error("Error fetching containers by master container:", error);
    return { data: [], error };
  }
}; 